/* eslint-disable react/jsx-no-useless-fragment */
/* eslint-disable jsx-a11y/img-redundant-alt */
import React from 'react';
import { Typography, Card } from '@mui/material';
import BackButton from '../BackButton';
import comprehesiveSafetySolution from "../../../../../../assets/images/ArticleImages/comprehesiveSafetySolution.webp";
import RoleOfDGMS from "../../../../../../assets/images/ArticleImages/Benefits-of-Adopting-DGMS-Compliant-Safety-Solutions-2-1.webp";
import WhyDGMS from "../../../../../../assets/images/ArticleImages/Future-of-Safety-in-Mines-with-DGMS-Compliance-2.webp";
import Challenges from "../../../../../../assets/images/ArticleImages/CHALLENGES-3.webp";
import  TechnologicalSolutions from "../../../../../../assets/images/ArticleImages/Benefits-of-Adopting-DGMS-Compliant-Safety-Solutions-3.webp";

// Define a type for the content blocks
type ContentBlock =
  | { type: 'heading'; value: string; variant?: 'h4' | 'h5' | 'h6' | 'subtitle2'; style?: React.CSSProperties }
  | { type: 'paragraph'; value: string; style?: React.CSSProperties }
  | { type: 'image'; src: string; alt?: string; style?: React.CSSProperties };

const ComprehesiveSafetySolutions: React.FC = () => {
  // Page header information
  const title = "Comprehensive Safety Solutions for Mines: Ensuring Compliance, Productivity, and Zero-Accident Operations";
  const headerImageSrc = comprehesiveSafetySolution;

  // Define the content of your article as a list of blocks.
  const contentBlocks: ContentBlock[] = [
    {
      type: 'paragraph',
      value: "Mining is one of the most hazardous industries worldwide, with workers facing risks from dangerous equipment, extreme weather conditions, and long hours. As mining companies increasingly face stringent safety regulations and strive to improve operational efficiency, the adoption of advanced safety technologies has become a necessity. This blog will explore the safety challenges in mining, the key technologies that address them, and the real-world impact these technologies have on mine safety, productivity, and compliance.",
    },
    {
      type: 'heading',
      value: "What is DGMS and Why Is It Required in Mining?",
      variant: 'h6',
      style: { marginTop: '16px', marginBottom: '8px' },
    },
    {
        type: 'paragraph',
        value: "The Directorate General of Mines Safety (DGMS) is a regulatory body under the Ministry of Labour and Employment, Government of India. It is responsible for ensuring the safety and health of workers in the mining industry by enforcing various safety regulations. DGMS conducts inspections and audits to ensure that mining operations comply with safety standards, and it issues guidelines for the safe operation of mines.",
      },
      {
        type: 'heading',
        value: "Role of DGMS in Mining:",
        variant: 'h6',
        style: { marginTop: '16px', marginBottom: '8px' },
      },
    {
      type: 'image',
      src: RoleOfDGMS,
      alt: "Connected vehicles schematic",
      style: { margin: '24px 0', width: '100%', height: 'auto', borderRadius: 12 },
    },
    {
        type: 'paragraph',
        value: (
          <>
            •<strong> Regulation and Compliance:</strong> DGMS establishes safety rules and standards for the mining industry to prevent accidents and fatalities. These guidelines cover aspects like ventilation, machinery safety, hazard prevention, worker protection, and emergency response.
          </>
        ) as unknown as string,
      },
      {
        type: 'paragraph',
        value: (
          <>
            •<strong> Inspection and Auditing:</strong> DGMS regularly inspects mines and conducts audits to ensure that mining companies are adhering to safety protocols. Non-compliance with DGMS regulations can result in penalties, shutdowns, or even suspension of operations.
          </>
        ) as unknown as string,
      },
      {
        type: 'paragraph',
        value: (
          <>
            •<strong> Training and Education:</strong> DGMS also provides training programs and safety seminars for workers and management to ensure they understand safety protocols and how to manage potential risks effectively.
          </>
        ) as unknown as string,
      },
      {
        type: 'heading',
        value: "Why DGMS Compliance is Critical for Mining Operations:",
        variant: 'h6',
        style: { marginTop: '16px', marginBottom: '8px' },
      },
      {
        type: 'image',
        src: WhyDGMS,
        alt: "Connected vehicles schematic",
        style: { margin: '24px 0', width: '100%', height: 'auto', borderRadius: 12 },
      },
      {
        type: 'paragraph',
        value: (
          <>
            •<strong> Worker Protection:</strong> Ensuring the safety of workers is the foremost priority in any mining operation. The mining environment is inherently dangerous, and strict adherence to DGMS regulations minimizes the risk of accidents and fatalities.
          </>
        ) as unknown as string,
      },
      {
        type: 'paragraph',
        value: (
          <>
            •<strong> Legal and Financial Consequences:</strong> Non-compliance with DGMS safety standards can lead to heavy penalties, operational shutdowns, or legal consequences. Regular inspections help avoid these risks and protect the company’s reputation.
          </>
        ) as unknown as string,
      },
      {
        type: 'paragraph',
        value: (
          <>
            •<strong> Increased Productivity:</strong> Mines that follow DGMS guidelines experience fewer accidents, leading to higher operational efficiency. Safe working conditions result in reduced downtime, fewer work stoppages, and better overall productivity.
          </>
        ) as unknown as string,
      },
      {
        type: 'paragraph',
        value: (
          <>
            •<strong> Better Insurance and Risk Management:</strong> By adhering to DGMS standards, mining companies may benefit from lower insurance premiums and reduced risk exposure. Insurers recognize that compliant companies have fewer accidents, making them more reliable.
          </>
        ) as unknown as string,
      },
      {
        type: 'heading',
        value: "The Role of Technology in DGMS Compliance:",
        variant: 'h6',
        style: { marginTop: '16px', marginBottom: '8px' },
      },
      {
        type: 'paragraph',
        value: "Technology plays a key role in helping mining companies meet DGMS requirements. With advanced safety solutions like Driver Monitoring Systems, Proximity Warning Systems, and Auto Dipping Systems, companies can ensure that their operations are compliant with safety standards, effectively reducing risks and improving compliance with DGMS regulations.",
      },
      {
        type: 'heading',
        value: "Challenges in Mine Safety",
        variant: 'h6',
        style: { marginTop: '16px', marginBottom: '8px' },
      },
      {
        type: 'image',
        src: Challenges,
        alt: "Connected vehicles schematic",
        style: { margin: '24px 0', width: '100%', height: 'auto', borderRadius: 12 },
      },
      {
        type: 'paragraph',
        value: "Mining operations present numerous safety hazards that need constant vigilance and innovation. The mining environment is inherently risky due to various factors such as terrain, weather, and human behavior.",
      },
      {
        type: 'heading',
        value: "1. Operator Fatigue and Inattention",
        variant: 'subtitle2',
        style: { marginTop: '16px', marginBottom: '8px' },
      },
      {
        type: 'paragraph',
        value: "Fatigue among mining workers is a significant contributor to accidents. Long shifts, monotonous tasks, and physical strain can lead to reduced alertness, increasing the likelihood of errors.",
      },
      {
        type: 'heading',
        value: "Contributing Factors:",
        variant: 'subtitle2',
        style: { marginTop: '16px', marginBottom: '8px' },
      },
      {
        type: 'paragraph',
        value: "Extended hours, physically demanding work, and low engagement levels contribute to fatigue. Lack of focus can cause operators to miss hazards, making them prone to accidents.",
      },
      {
        type: 'heading',
        value: "Real-World Example:",
        variant: 'subtitle2',
        style: { marginTop: '16px', marginBottom: '8px' },
      },
      {
        type: 'paragraph',
        value: "A mine truck operator suffering from fatigue might fail to spot a road obstruction or another vehicle, leading to dangerous collisions.",
      },
      {
        type: 'heading',
        value: "2. Visibility Hazards in Night Operations",
        variant: 'subtitle2',
        style: { marginTop: '16px', marginBottom: '8px' },
      },
      {
        type: 'paragraph',
        value: "Many mines operate 24/7, often requiring workers to operate machinery in low-light conditions. Poor visibility can severely impair a worker’s ability to detect nearby hazards, leading to accidents.",
      },
      {
        type: 'heading',
        value: "Contributing Factors:",
        variant: 'subtitle2',
        style: { marginTop: '16px', marginBottom: '8px' },
      },
      {
        type: 'paragraph',
        value: "Limited lighting, dust, and the glare from headlights can obstruct visibility, particularly during night shifts or in underground operations.",
      },
      {
        type: 'heading',
        value: "Real-World Example:",
        variant: 'subtitle2',
        style: { marginTop: '16px', marginBottom: '8px' },
      },
      {
        type: 'paragraph',
        value: "A haul truck might collide with another vehicle on the same path due to reduced visibility caused by poor lighting, resulting in equipment damage and worker injuries.",
      },
      {
        type: 'heading',
        value: "3. Collision Risks Due to Blind Spots",
        variant: 'subtitle2',
        style: { marginTop: '16px', marginBottom: '8px' },
      },
      {
        type: 'paragraph',
        value: "Mining machinery, such as dump trucks and excavators, are large and often operate in tight, confined spaces. Blind spots are common, and when vehicles reverse or maneuver in these areas, the risk of collision is high.",
      },
      {
        type: 'heading',
        value: "Contributing Factors:",
        variant: 'subtitle2',
        style: { marginTop: '16px', marginBottom: '8px' },
      },
      {
        type: 'paragraph',
        value: "Large machinery, restricted sightlines, and a high density of vehicles and personnel increase the risk of accidents.",
      },
      {
        type: 'heading',
        value: "Real-World Example:",
        variant: 'subtitle2',
        style: { marginTop: '16px', marginBottom: '8px' },
      },
      {
        type: 'paragraph',
        value: "A mining dump truck reversing in a narrow alley might collide with another vehicle or a worker due to limited visibility in its blind spot.",
      },
      {
        type: 'heading',
        value: "4. Regulatory Compliance and Costly Penalties",
        variant: 'subtitle2',
        style: { marginTop: '16px', marginBottom: '8px' },
      },
      {
        type: 'paragraph',
        value: "Mining companies are held to strict safety standards set by regulatory bodies such as the Directorate General of Mines Safety (DGMS). Non-compliance with these regulations can lead to significant penalties, shutdowns, or legal consequences.",
      },
      {
        type: 'heading',
        value: "Real-World Example:",
        variant: 'subtitle2',
        style: { marginTop: '16px', marginBottom: '8px' },
      },
      {
        type: 'paragraph',
        value: "A company operating in violation of safety protocols could face penalties, leading to downtime, reduced productivity, and damage to its reputation.",
      },
      {
        type: 'heading',
        value: "Technological Solutions to Overcome Mining Safety Challenges",
        variant: 'h6',
        style: { marginTop: '16px', marginBottom: '8px' },
      },
      {
        type: 'image',
        src: TechnologicalSolutions,
        alt: "Connected vehicles schematic",
        style: { margin: '24px 0', width: '100%', height: 'auto', borderRadius: 12 },
      },
      {
        type: 'paragraph',
        value: "To tackle these challenges head-on, mining companies are increasingly integrating technology-driven safety solutions. These technologies are designed to enhance safety, improve productivity, and ensure compliance with ever-tightening regulations.",
      },
      {
        type: 'heading',
        value: "1. Driver Monitoring System (DMS)",
        variant: 'subtitle2',
        style: { marginTop: '16px', marginBottom: '8px' },
      },
      {
        type: 'paragraph',
        value: "Operator fatigue remains a leading cause of accidents in mining. The Driver Monitoring System (DMS) uses advanced AI and camera systems to monitor driver alertness and detect signs of fatigue or distraction.",
      },
      {
        type: 'heading',
        value: "Key Features:",
        variant: 'subtitle2',
        style: { marginTop: '16px', marginBottom: '8px' },
      },
      {
        type: 'paragraph',
        value: "• Real-time detection of drowsiness, distraction, and inattention.",
      },
      {
        type: 'paragraph',
        value: "• AI-driven alerts to notify drivers and supervisors when fatigue is detected.",
      },
      {
        type: 'paragraph',
        value: "• Non-intrusive monitoring that doesn’t require wearables or manual intervention.",
      },
      {
        type: 'heading',
        value: "Impact on Safety and Productivity:",
        variant: 'subtitle2',
        style: { marginTop: '16px', marginBottom: '8px' },
      },
      {
        type: 'paragraph',
        value: (
          <>
            •<strong> Reduction in Fatigue-Related Accidents:</strong> By alerting operators when they show signs of fatigue, the DMS reduces the likelihood of accidents caused by human error, ensuring a safer workplace.
          </>
        ) as unknown as string,
      },
      {
        type: 'paragraph',
        value: (
          <>
            •<strong> Improved Productivity:</strong> With a more alert workforce, productivity improves as operators are better equipped to make quick, accurate decisions.
          </>
        ) as unknown as string,
      },
      {
        type: 'heading',
        value: "Real-World Example:",
        variant: 'subtitle2',
        style: { marginTop: '16px', marginBottom: '8px' },
      },
      {
        type: 'paragraph',
        value: "In a mining operation, a truck driver is alerted when showing signs of drowsiness, preventing a potential collision and keeping operations on track.",
      },
      {
        type: 'heading',
        value: "2. Auto Dipping System",
        variant: 'subtitle2',
        style: { marginTop: '16px', marginBottom: '8px' },
      },
      {
        type: 'paragraph',
        value: "Mining operations often take place at night or in poorly lit areas, where glare from headlights can obstruct visibility. The Auto Dipping System automatically adjusts vehicle headlights to reduce glare and improve overall visibility.",
      },
      {
        type: 'heading',
        value: "Key Features:",
        variant: 'subtitle2',
        style: { marginTop: '16px', marginBottom: '8px' },
      },
      {
        type: 'paragraph',
        value: "• Automatic adjustment of headlights based on oncoming traffic.",
      },
      {
        type: 'paragraph',
        value: "• Real-time visibility optimization to ensure clear sightlines even in dark, dusty conditions.",
      },
      {
        type: 'paragraph',
        value: "• Seamless integration with mining vehicles for easy operation.",
      },
      {
        type: 'heading',
        value: "Impact on Safety and Productivity:",
        variant: 'subtitle2',
        style: { marginTop: '16px', marginBottom: '8px' },
      },
      {
        type: 'paragraph',
        value: (
          <>
            •<strong> Enhanced Visibility and Reduced Collisions:</strong> By improving visibility, especially during night shifts, this system reduces the risk of accidents caused by glare and poor lighting.

          </>
        ) as unknown as string,
      },
      {
        type: 'paragraph',
        value: (
          <>
            •<strong> Regulatory Compliance:</strong> The system helps operators meet visibility and lighting standards set by regulatory authorities, avoiding fines and penalties.
          </>
        ) as unknown as string,
      },
      {
        type: 'heading',
        value: "Real-World Example:",
        variant: 'subtitle2',
        style: { marginTop: '16px', marginBottom: '8px' },
      },
      {
        type: 'paragraph',
        value: "During night shifts, the auto dipping system adjusts the headlights of an underground mining truck, preventing glare for other vehicles and workers in the area, ensuring smooth, safe operations.",
      },
      {
        type: 'heading',
        value: "3. Proximity Warning System",
        variant: 'subtitle2',
        style: { marginTop: '16px', marginBottom: '8px' },
      },
      {
        type: 'paragraph',
        value: "Mining environments are often crowded, with various machines and personnel working in close proximity. The Proximity Warning System uses radar and sensor technology to detect nearby obstacles and provide real-time alerts to operators, minimizing the risk of collisions.",
      },
      {
        type: 'heading',
        value: "Key Features:",
        variant: 'subtitle2',
        style: { marginTop: '16px', marginBottom: '8px' },
      },
      {
        type: 'paragraph',
        value: "• Radar-based sensors detect objects, vehicles, and personnel within a defined range.",
      },
      {
        type: 'paragraph',
        value: "• Real-time alerts are sent to the operator when an object is detected, preventing collisions.",
      },
      {
        type: 'paragraph',
        value: "• Integration with other safety systems for a holistic safety approach.",
      },
      {
        type: 'heading',
        value: "Impact on Safety and Productivity:",
        variant: 'subtitle2',
        style: { marginTop: '16px', marginBottom: '8px' },
      },
      {
        type: 'paragraph',
        value: (
          <>
            •<strong> Reduced Collisions and Injuries: </strong> Proximity sensors prevent accidents by alerting operators about nearby vehicles and workers, reducing the likelihood of dangerous collisions.

          </>
        ) as unknown as string,
      },
      {
        type: 'paragraph',
        value: (
          <>
            •<strong> Enhanced Operational Efficiency:</strong> With fewer accidents and disruptions, mining operations can continue smoothly, leading to better productivity.
          </>
        ) as unknown as string,
      },
      {
        type: 'heading',
        value: "Real-World Example:",
        variant: 'subtitle2',
        style: { marginTop: '16px', marginBottom: '8px' },
      },
      {
        type: 'paragraph',
        value: "A large excavator working in a busy mining area receives an alert when a worker enters its proximity, prompting the operator to stop and avoid a potential collision.",
      },
      {
        type: 'heading',
        value: "The Path Forward: A Safer, More Efficient Mining Industry",
        variant: 'subtitle2',
        style: { marginTop: '16px', marginBottom: '8px' },
      },
      {
        type: 'paragraph',
        value: "The real-world impact of these safety technologies is profound. Mines that implement solutions such as Driver Monitoring Systems, Auto Dipping Systems, and Proximity Warning Systems witness a significant reduction in workplace accidents, fatalities, and operational inefficiencies. These systems are not just about compliance; they fundamentally change the mining environment by fostering a culture of safety, reducing risks, and improving overall productivity.",
      },
      {
        type: 'paragraph',
        value: "With fewer accidents and operational disruptions, mining companies experience less downtime, which translates to higher output and better bottom-line results. Additionally, these solutions help mining companies maintain regulatory compliance, avoid fines, and enhance their reputation as safe and reliable employers.",
      },
      {
        type: 'paragraph',
        value: "Further more, these technologies also have financial benefits: they reduce the number of insurance claims, lower premiums, and mitigate the potential for costly legal issues. As a result, companies can realize long-term savings while achieving a safer, more efficient workplace.",
      },
      {
        type: 'paragraph',
        value: "At the forefront of these advancements is Novus Hi-Tech, a leader in AI-driven safety solutions for mining. Their comprehensive, DGMS-compliant safety system is designed to meet the unique needs of the mining sector, offering cutting-edge technology to protect workers, improve productivity, and ensure compliance.",
      },
      {
        type: 'paragraph',
        value: "By integrating Novus Hi-Tech’s innovative solutions into their operations, mining companies can enhance worker safety, reduce costs associated with accidents, and achieve a zero-accident workplace. With Novus Hi-Tech’s commitment to advancing safety in the mining industry, the path to a safer, more efficient future is clearer than ever.",
      },
  ];

  // Function to render each content block based on its type.
  const renderContentBlock = (block: ContentBlock, index: number) => {
    switch (block.type) {
      case 'heading':
        return (
          <Typography
            key={index}
            variant={block.variant || 'h4'}
            style={{ fontWeight: 600, ...block.style }}
          >
            {block.value}
          </Typography>
        );
      case 'paragraph':
        return (
          <Typography
            key={index}
            variant="body1"
            style={{
              fontSize: '16px',
              fontWeight: 400,
              lineHeight: '24px',
              // color: 'gray',
              ...block.style,
            }}
          >
            {block.value}
          </Typography>
        );
      case 'image':
        return (
          <Card
            key={index}
            style={{
              overflow: 'hidden',
              borderRadius: 12,
              ...block.style,
            }}
          >
            <img
              src={block.src}
              alt={block.alt || 'Article image'}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </Card>
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col w-full">
      <BackButton />

      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', alignItems: 'center' }}>
        {/* Header Section */}
        <div
          style={{
            width: '100%',
            maxWidth: '766px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          {/* Title & Tag Section */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Typography variant="h4" style={{ fontWeight: 600 }}>
              {title}
            </Typography>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '8px',
              }}
            >
            </div>
          </div>

          {/* Header Image */}
          <Card
            style={{
              width: '100%',
              height: 384,
              borderRadius: 12,
              overflow: 'hidden',
            }}
          >
            <img
              src={headerImageSrc}
              alt="Article header image"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </Card>
        </div>

        {/* Content Section */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            width: '100%',
            maxWidth: '766px',
            padding: '16px 0',
          }}
        >
          {contentBlocks.map((block, index) => renderContentBlock(block, index))}
        </div>
      </div>
    </div>
  );
};

export default ComprehesiveSafetySolutions;
